import { cameraGeometry } from '@/infrastructure/engine3d/camera/cameraGeometry';
import { Easing, Tween } from '@tweenjs/tween.js';
import { PerspectiveCamera, Vector3 } from 'three';
import type { ICameraControls } from '@/infrastructure/engine3d/camera/controls/ICameraControls';
import type { TCameraParams } from '@/infrastructure/engine3d/camera/TCameraParams';

// Abstract PerspectiveCamera + controls wrapper. Exposes spherical
// coord setters (setPhi/setTheta/setRadius) for subclasses to reframe.
abstract class AAnimatedCamera extends PerspectiveCamera {
    protected _cameraControls: ICameraControls;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _currentTweenSpherical: Tween<any> | undefined;

    get controls(): ICameraControls {
        return this._cameraControls;
    }

    constructor(cameraParams: TCameraParams) {
        super(
            cameraParams.fov || 75,
            cameraParams.aspect || cameraParams.rendererDomElement.width / cameraParams.rendererDomElement.height,
            cameraParams.near || 0.01,
            cameraParams.far || 1000,
        );
        if (cameraParams?.name) {
            this.name = cameraParams.name;
        }
        this._cameraControls = this.createControls(cameraParams.rendererDomElement);
    }

    protected abstract createControls(rendererDomElement: HTMLCanvasElement): ICameraControls;

    protected initializeControls(): void {
        this._cameraControls.addEventListener('change', () => {
            if (this._currentTweenSpherical?.isPlaying()) {
                this._currentTweenSpherical?.stop();
                const savedQuaternion = this.quaternion.clone();
                this._cameraControls.update();
                this.quaternion.copy(savedQuaternion);
            }
        });
    }

    setSphericalCoordinates(params: {
        animate?: boolean;
        duration?: number;
        phi?: number;
        radius?: number;
        theta?: number;
    }): Promise<void> {
        return new Promise((resolve) => {
            this._currentTweenSpherical?.stop();

            const theta = params.theta ?? this.theta;
            const phi = params.phi ?? this.phi;
            const radius = params.radius || this.radius;
            const animate = params.animate || false;
            const duration = params.duration || 400;

            const targetPosition = cameraGeometry.cartesianFromSpherical(radius, phi, theta);

            const currentRotation = this.quaternion.clone();
            const currentPosition = this.position.clone();
            this.position.copy(targetPosition).add(this.controls.target);
            this.lookAt(this.controls.target);
            const targetRotation = this.quaternion.clone();
            this.position.copy(currentPosition);
            this.quaternion.copy(currentRotation);

            const ending = () => {
                this.position.copy(targetPosition).add(this.controls.target);
                this.quaternion.copy(targetRotation);
                resolve();
            };
            if (animate) {
                const anim = { t: 0, radius: this.radius };
                this._currentTweenSpherical = new Tween(anim)
                    .to({ t: 1, radius }, duration)
                    .onUpdate(() => {
                        this.quaternion.slerp(targetRotation, anim.t);
                        this.position.copy(new Vector3(0, 0, anim.radius).applyQuaternion(this.quaternion)).add(this.controls.target);
                    })
                    .onComplete(ending)
                    .easing(Easing.Quadratic.InOut)
                    .start();
            } else {
                ending();
            }
        });
    }

    get phi(): number {
        return this.position
            .clone()
            .sub(this.controls.target)
            .angleTo(new Vector3(0, 1, 0));
    }

    setPhi(phi: number, animate: boolean = false, duration: number = 400): Promise<void> {
        return this.setSphericalCoordinates({ phi, animate, duration });
    }

    get theta(): number {
        const direction = this.position.clone().sub(this.controls.target);
        direction.y = 0;
        const angle = direction.angleTo(new Vector3(0, 0, 1));
        return direction.x < 0 ? -angle : angle;
    }

    setTheta(theta: number, animate: boolean = false, duration: number = 400): Promise<void> {
        return this.setSphericalCoordinates({ theta, animate, duration });
    }

    get radius(): number {
        return this.position.distanceTo(this.controls.target);
    }

    setRadius(radius: number, animate: boolean = false, duration: number = 400): Promise<void> {
        return this.setSphericalCoordinates({ radius, animate, duration });
    }

    render(): void {
        this._currentTweenSpherical?.update();
    }
}

export { AAnimatedCamera };
